// ============================================================
// usePortalFactory — live data hooks for the Olivium Project Portal Factory.
//
// Honesty: these hooks NEVER seed a demo portal, module, action, note,
// blocker, or receipt. Everything comes from the API. No bearer token →
// authRequired (the pre-sign-in state). A missing migration surfaces as an
// honest "backend not migrated" message; an empty result is an honest empty
// state.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { isAuthError } from '../../../../api/client'
import {
  listPortals, getPortal, humanizePortalError,
  getPortalContext, upsertPortalContext, setPortalClientVisible,
  type PortalRecord, type PortalDetail, type PortalContextRecord, type PortalContextDraft,
} from './portalFactory'

export interface PortalsState {
  portals: PortalRecord[]
  loading: boolean
  error: string | null
  authRequired: boolean
}

export function usePortals() {
  const [state, setState] = useState<PortalsState>({ portals: [], loading: true, error: null, authRequired: false })

  const load = useCallback(async (signal?: AbortSignal) => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const portals = await listPortals(signal)
      if (signal?.aborted) return
      setState({ portals, loading: false, error: null, authRequired: false })
    } catch (err) {
      if (signal?.aborted) return
      if (isAuthError(err)) setState({ portals: [], loading: false, error: null, authRequired: true })
      else setState({ portals: [], loading: false, error: humanizePortalError(err), authRequired: false })
    }
  }, [])

  useEffect(() => {
    const ac = new AbortController()
    load(ac.signal)
    return () => ac.abort()
  }, [load])

  return { ...state, reload: () => load() }
}

export interface PortalContextState {
  context: PortalContextRecord | null
  loading: boolean
  error: string | null
  authRequired: boolean
}

/** Loads a portal's project/repo context (PHASE_2L). Honest states; the
 *  context row may be null (nothing saved yet) — that is an empty state, not
 *  an error. */
export function usePortalContext(id: string | undefined) {
  const [state, setState] = useState<PortalContextState>({ context: null, loading: true, error: null, authRequired: false })

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!id) { setState({ context: null, loading: false, error: null, authRequired: false }); return }
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const context = await getPortalContext(id, signal)
      if (signal?.aborted) return
      setState({ context, loading: false, error: null, authRequired: false })
    } catch (err) {
      if (signal?.aborted) return
      if (isAuthError(err)) setState({ context: null, loading: false, error: null, authRequired: true })
      else setState({ context: null, loading: false, error: humanizePortalError(err), authRequired: false })
    }
  }, [id])

  useEffect(() => { const ac = new AbortController(); load(ac.signal); return () => ac.abort() }, [load])

  const save = useCallback(async (fields: PortalContextDraft) => {
    if (!id) return
    await upsertPortalContext(id, fields)
    await load()
  }, [id, load])

  const publish = useCallback(async (visible: boolean) => {
    if (!id) return
    await setPortalClientVisible(id, visible)
    await load()
  }, [id, load])

  return { ...state, reload: () => load(), save, publish }
}

export interface PortalDetailState {
  detail: PortalDetail | null
  loading: boolean
  error: string | null
  authRequired: boolean
  notFound: boolean
}

export function usePortal(id: string | undefined) {
  const [state, setState] = useState<PortalDetailState>({ detail: null, loading: true, error: null, authRequired: false, notFound: false })

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!id) { setState({ detail: null, loading: false, error: null, authRequired: false, notFound: true }); return }
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const detail = await getPortal(id, signal)
      if (signal?.aborted) return
      setState({ detail, loading: false, error: null, authRequired: false, notFound: false })
    } catch (err) {
      if (signal?.aborted) return
      if (isAuthError(err)) { setState({ detail: null, loading: false, error: null, authRequired: true, notFound: false }); return }
      const code = (err as { code?: string; status?: number })?.code
      const status = (err as { status?: number })?.status
      if (code === 'not_found' || status === 404) { setState({ detail: null, loading: false, error: null, authRequired: false, notFound: true }); return }
      setState({ detail: null, loading: false, error: humanizePortalError(err), authRequired: false, notFound: false })
    }
  }, [id])

  useEffect(() => {
    const ac = new AbortController()
    load(ac.signal)
    return () => ac.abort()
  }, [load])

  return { ...state, reload: () => load() }
}
